import React, { useContext } from "react";
import { ThemeContext } from "../theme";

const serviceCards = [
  {
    title: "Data Analytics",
    description:
      "Transform raw data into actionable intelligence. Our AI-powered analytics bots uncover patterns, predict trends, and narrate insights with clarity."
  },
  {
    title: "Knowledge & Insights",
    description:
      "Synthesize information at speed. Knowledge bots gather, connect dots, and surface insights so your team can learn, iterate, and ship faster."
  },
  {
    title: "Productivity",
    description:
      "Automate the dull, amplify the bold. Productivity bots streamline workflows, guard context, and let humans stay focused on the elixir."
  }
];

const themeOptions = [
  { id: "dark", label: "Dark Mode" },
  { id: "retro", label: "Retro Mode" }
];

const styles = {
  leanSection: {
    maxWidth: 1100,
    margin: "32px auto",
    padding: "0 1.5rem",
    textAlign: "center"
  },
  hero: {
    marginBottom: 24
  },
  heroTitle: {
    margin: 0,
    fontSize: "3rem",
    fontWeight: 700,
    background: "linear-gradient(90deg, #FF6661 0%, #B86FFF 50%, #4D44DC 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent"
  },
  heroSubtitle: {
    marginTop: "0.5rem",
    fontSize: "1.15rem",
    color: "#A1A1AA"
  },
  copyBlock: {
    marginTop: "2rem",
    fontSize: "1.1rem",
    color: "#FFFFFF",
    lineHeight: 1.8,
    maxWidth: 900,
    marginLeft: "auto",
    marginRight: "auto"
  },
  serviceGrid: {
    marginTop: "3rem",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "1.5rem",
    justifyContent: "center"
  },
  card: {
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 18,
    padding: "1.5rem",
    background: "rgba(255,255,255,0.03)",
    boxShadow: "0 20px 40px rgba(0,0,0,0.35)",
    minHeight: 200,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.75rem"
  },
  cardTitle: {
    margin: 0
  },
  cardBody: {
    margin: 0,
    color: "#B0B6C7",
    fontSize: "0.95rem",
    lineHeight: 1.7
  },
  footer: {
    marginTop: "4rem",
    paddingTop: "2rem",
    borderTop: "1px solid rgba(255,255,255,0.08)"
  },
  footerText: {
    margin: 0,
    color: "#A1A1AA"
  },
  footerSecondary: {
    margin: "0.4rem 0 0",
    color: "#6C6E72"
  },
  footerAccent: {
    marginTop: "1rem",
    color: "#57585C",
    fontSize: "0.85rem"
  }
};

styles.themeRow = {
  margin: "2rem auto",
  display: "flex",
  justifyContent: "center",
  gap: "1rem"
};

styles.themeButton = {
  padding: "8px 18px",
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,0.3)",
  background: "transparent",
  color: "white",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s ease"
};

styles.themeButtonActive = {
  background: "linear-gradient(120deg, rgba(255,102,97,0.3), rgba(184,111,255,0.3))",
  borderColor: "#B86FFF"
};

styles.assetButtonWrapper = {
  display: "flex",
  justifyContent: "center",
  marginTop: "2rem",
  paddingBottom: "2rem"
};

styles.assetButton = {
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,0.5)",
  padding: "10px 26px",
  background: "linear-gradient(135deg, rgba(255,243,179,0.25), rgba(255,102,97,0.25))",
  color: "#fff8f2",
  fontWeight: 600,
  boxShadow: "0 12px 35px rgba(255,102,97,0.25)",
  cursor: "pointer",
  textTransform: "uppercase",
  letterSpacing: 1.2,
  transition: "transform 0.2s ease, box-shadow 0.2s ease"
};

const HomePage = ({ onLaunchAssets }) => {
  const { theme, setTheme } = useContext(ThemeContext);
  return (
    <section style={styles.leanSection}>
    <div style={styles.hero}>
      <img
        src="/images/twodots_logo.png"
        alt="TwoDots logo"
        style={{ width: 160, height: "auto", marginBottom: 8, objectFit: "contain" }}
      />
      <h1 style={styles.heroTitle}>TwoDots</h1>
      <p style={styles.heroSubtitle}>Continuous Extension. Infinite Possibilities.</p>
    </div>
    <div style={styles.themeRow}>
      {themeOptions.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => setTheme(option.id)}
          style={{
            ...styles.themeButton,
            ...(theme === option.id ? styles.themeButtonActive : {})
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
    <p style={styles.copyBlock}>
      Welcome to TwoDots, where AI meets boundless innovation. Like the two dots that symbolize endless continuation, we believe in the power of “and
      more…” — constantly evolving, expanding, and discovering new horizons. Our AI services and intelligent bots are designed to transform how you work,
      think, and create, opening doors to possibilities you haven’t imagined yet.
    </p>
    <div style={styles.serviceGrid}>
      {serviceCards.map((card) => (
        <article key={card.title} style={styles.card}>
          <h3 style={styles.cardTitle}>{card.title}</h3>
          <p style={styles.cardBody}>{card.description}</p>
        </article>
      ))}
    </div>
    <div style={styles.assetButtonWrapper}>
      <button
        type="button"
        onClick={() => onLaunchAssets?.()}
        style={styles.assetButton}
      >
        Explore Assets
      </button>
    </div>
    <footer style={styles.footer}>
      <p style={styles.footerText}>TwoDots • AI Services & Bots</p>
      <p style={styles.footerSecondary}>www.twodots.cloud</p>
      <p style={styles.footerAccent}>Powered by continuous innovation and infinite possibilities.</p>
    </footer>
    </section>
  );
};

export default HomePage;
